import { Dictionary, keyBy } from 'lodash';

import { MealConstant } from '../constants';
import { DateHelper } from './date.helper';
import { Meal, MealEndTimes, MealName, MealTime, SnackName } from '../models';

export class MealHelper {
  // Meal Names
  private static readonly ALL_MEAL_NAMES: Array<MealName | SnackName> = [
    MealName.Breakfast,
    SnackName.Snack_AM,
    MealName.Lunch,
    SnackName.Snack_PM,
    MealName.Dinner,
    SnackName.Snack_HS
  ];

  // Meal Short Name Map
  private static readonly MEAL_SHORT_NAME_MAP = {
    am: 'Brk',
    pm: 'Lun',
    hs: 'Din'
  };

  // Generate definitions
  private static readonly _meals: Meal[] = MealHelper.ALL_MEAL_NAMES.map<Meal>(
    (name, inx) => {
      const MealID = inx + 1;
      const keyName = name.toLowerCase();
      const [type] = MealID % 2 === 0 ? ['snack', -2] : ['meal', -1];
      const time = MealID > 2 ? (MealID > 4 ? 'hs' : 'pm') : 'am';
      const shortName = MealHelper.MEAL_SHORT_NAME_MAP[time];
      const bigTime = time.toUpperCase();
      const suffix = bigTime + shortName;

      return {
        name,
        keyName,
        type,
        time,
        suffix,
        shortName,
        id: MealID
      };
    }
  );

  static readonly ALL_MEALS_BY_ID: Dictionary<Meal> = keyBy(
    MealHelper._meals,
    'id'
  );
  static readonly MEALS: Meal[] = MealHelper._meals.filter(
    (i) => i.type === 'meal'
  );

  static getMealIdByTime(
    meal_end_times: MealEndTimes,
    facilityTimeZone?: string
  ): MealTime {
    const mealId = MealHelper.getMealIdWithCutoffTime(
      meal_end_times,
      facilityTimeZone
    );
    return mealId || MealConstant.BREAKFAST_ID;
  }

  static getMealIdWithCutoffTime(
    meal_end_times: MealEndTimes,
    facilityTimeZone?: string
  ): MealTime {
    const { hour, minute } = MealHelper.getCurrentTimeInZone(facilityTimeZone);
    const currentMinutes = hour * 60 + minute;

    const breakfastEnd = meal_end_times?.breakfast
      ? MealHelper.timeToMinutes(meal_end_times.breakfast)
      : 10 * 60 + 15; // default: 10:15
    const lunchEnd = meal_end_times?.lunch
      ? MealHelper.timeToMinutes(meal_end_times.lunch)
      : 14 * 60 + 30; // default: 14:30
    const dinnerEnd = meal_end_times?.dinner
      ? MealHelper.timeToMinutes(meal_end_times.dinner)
      : 23 * 60; // default: 23:00

    if (currentMinutes < breakfastEnd) {
      return MealConstant.BREAKFAST_ID;
    } else if (currentMinutes < lunchEnd) {
      return MealConstant.LUNCH_ID;
    } else if (currentMinutes < dinnerEnd) {
      return MealConstant.DINNER_ID;
    }

    return null;
  }

  /**
   * Returns the current hour and minute in the given IANA timezone.
   * Falls back to device local time if timezone is absent or invalid.
   */
  static getCurrentTimeInZone(facilityTimeZone?: string): {
    hour: number;
    minute: number;
  } {
    if (facilityTimeZone) {
      try {
        const options = <Intl.DateTimeFormatOptions>{
          timeZone: facilityTimeZone,
          hourCycle: 'h23',
          hour: 'numeric',
          minute: 'numeric'
        };
        const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(
          new Date()
        );
        return {
          hour: parseInt(
            parts.find((p) => p.type === 'hour')?.value ?? '0',
            10
          ),
          minute: parseInt(
            parts.find((p) => p.type === 'minute')?.value ?? '0',
            10
          )
        };
      } catch {
        console.warn(
          `Invalid facility timezone: ${facilityTimeZone}, using device local time`
        );
      }
    }
    const now = new Date();
    return { hour: now.getHours(), minute: now.getMinutes() };
  }

  /**
   * Returns today's date as a yyyy-MM-dd string in the given IANA timezone.
   * Falls back to device local date if timezone is absent or invalid.
   */
  static getCurrentDateInZone(facilityTimeZone?: string): string {
    if (facilityTimeZone) {
      try {
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: facilityTimeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).formatToParts(new Date());
        const y = parts.find((p) => p.type === 'year')?.value;
        const m = parts.find((p) => p.type === 'month')?.value;
        const d = parts.find((p) => p.type === 'day')?.value;
        return `${y}-${m}-${d}`;
      } catch {
        console.warn(
          `Invalid facility timezone: ${facilityTimeZone}, using device local date`
        );
      }
    }
    return DateHelper.format(new Date());
  }

  private static timeToMinutes(timeStr: string): number {
    const [hour, minute] = timeStr.split(':').map(Number);
    return hour * 60 + minute;
  }

  static readonly MEAL_NAME_MAPPER: Record<MealTime, string> = {
    [MealConstant.BREAKFAST_ID]: 'Breakfast',
    [MealConstant.LUNCH_ID]: 'Lunch',
    [MealConstant.DINNER_ID]: 'Dinner'
  };

  static readonly MEAL_TRANSLATION_KEY_MAPPER: Record<MealTime, string> = {
    [MealConstant.BREAKFAST_ID]: 'meals.breakfast',
    [MealConstant.LUNCH_ID]: 'meals.lunch',
    [MealConstant.DINNER_ID]: 'meals.dinner'
  };
}
